import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, MapPin, Home, Maximize2, Wrench, CalendarClock, Coins, BarChart3, Tag, Layers, TrendingUp, FileText,
  Handshake, UserRound, Info, X, Loader2, CheckCircle2, Building2, ListChecks,
} from 'lucide-react';
import { EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import FundingBar from '@/components/opportunities/FundingBar';
import {
  opportunitiesApi, STATUS_STYLE, imgUrl, money, opTitle, opPlace, returnFigure, saleFigure, disclaimer, statusLabel,
} from '@/services/opportunitiesApi';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Ficha completa de una oportunidad (/dashboard/propiedades/:id): fotos, ficha técnica, cifras estimadas,
  estructura de costes, estudio de mercado y aportaciones (barra de progreso, mínimo y solicitud de participación).
*/

// Escenario (texto libre de la ficha): los habituales en inglés; si no, se muestra tal cual
const SCENARIO_EN = { conservador: 'Conservative', moderado: 'Moderate', base: 'Base', optimista: 'Optimistic', pesimista: 'Pessimistic' };

const T = {
  es: {
    photo: 'Foto',
    toastSent: 'Solicitud enviada. El equipo del club se pondrá en contacto contigo.',
    request: 'Solicitar participación',
    close: 'Cerrar',
    amountLabel: 'Aportación que te planteas',
    minLabel: 'Aportación mínima',
    comment: 'Comentario (opcional)',
    accept: 'Entiendo que esta solicitud no es vinculante ni constituye una aportación. Si sigo adelante, la participación se formaliza con el contrato correspondiente y la documentación completa de la operación.',
    cancel: 'Cancelar',
    send: 'Enviar solicitud',
    back: 'Oportunidades',
    notFound: 'No hemos encontrado esta oportunidad',
    scenario: (sc) => `Escenario ${sc.toLowerCase()}`,
    facts: 'Ficha de la operación',
    location: 'Ubicación',
    assetType: 'Tipo de activo',
    surface: 'Superficie',
    projectType: 'Estado del proyecto',
    term: 'Plazo estimado del proyecto',
    units: 'Unidades',
    totalInvestment: 'Inversión total estimada',
    zoneAvg: 'Precio medio de la zona',
    costs: 'Inversión necesaria y estructura de costes',
    costTotal: 'Total inversión',
    costNote: 'Estimación aproximada de los gastos de la operación.',
    vehicle: 'Vehículo',
    operator: 'Operador',
    partnerText: (p) => `Operación presentada por Freedom Homes Club en colaboración con ${p}.`,
    market: 'Estudio del municipio y referencias de mercado',
    asset: 'Activo',
    avgPrice: 'P.V.P. mercado medio',
    range: 'Rango',
    funding: 'Participación en la operación',
    currency: 'Divisa',
    usd: 'Dólares ($)',
    eur: 'Euros (€)',
    accessMin: (v) => `Mínimo para socios Access. Con PRO o Premium puedes aportar desde ${v}.`,
    target: 'Objetivo de aportaciones:',
    sentFor: (v) => `Solicitud enviada por ${v}. El equipo del club se pondrá en contacto contigo.`,
    change: 'Modificar mi solicitud',
    full: 'Esta operación ya está financiada al 100 %.',
    closed: 'Esta operación no admite nuevas solicitudes en este momento.',
    footer: 'Las aportaciones se formalizan mediante contrato privado con la documentación completa de la operación. Cada socio decide en qué operación participa.',
  },
  en: {
    photo: 'Photo',
    toastSent: 'Request sent. The club team will be in touch.',
    request: 'Request to take part',
    close: 'Close',
    amountLabel: 'Contribution you’re considering',
    minLabel: 'Minimum contribution',
    comment: 'Comment (optional)',
    accept: 'I understand that this request is not binding and is not a contribution. If I go ahead, my participation is formalised through the relevant contract and the deal’s full documentation.',
    cancel: 'Cancel',
    send: 'Send request',
    back: 'Opportunities',
    notFound: 'We couldn’t find this opportunity',
    scenario: (sc) => (SCENARIO_EN[sc.trim().toLowerCase()] ? `${SCENARIO_EN[sc.trim().toLowerCase()]} scenario` : `Scenario: ${sc}`),
    facts: 'Deal overview',
    location: 'Location',
    assetType: 'Asset type',
    surface: 'Area',
    projectType: 'Project stage',
    term: 'Estimated project term',
    units: 'Units',
    totalInvestment: 'Estimated total investment',
    zoneAvg: 'Average price in the area',
    costs: 'Capital required and cost breakdown',
    costTotal: 'Total investment',
    costNote: 'Approximate estimate of the deal’s costs.',
    vehicle: 'Vehicle',
    operator: 'Operator',
    partnerText: (p) => `Deal presented by Freedom Homes Club in collaboration with ${p}.`,
    market: 'Local area study and market references',
    asset: 'Asset',
    avgPrice: 'Average market price',
    range: 'Range',
    funding: 'Taking part in this deal',
    currency: 'Currency',
    usd: 'US dollars ($)',
    eur: 'Euros (€)',
    accessMin: (v) => `Minimum for Access members. With PRO or Premium you can contribute from ${v}.`,
    target: 'Contribution target:',
    sentFor: (v) => `Request sent for ${v}. The club team will be in touch.`,
    change: 'Change my request',
    full: 'This deal is now fully funded.',
    closed: 'This deal isn’t accepting new requests at the moment.',
    footer: 'Contributions are formalised through a private contract, with the deal’s full documentation. Each member decides which deals to take part in.',
  },
};

const card = 'rounded-2xl border border-white/[0.06] bg-white/[0.02]';

function Gallery({ images, title, t }) {
  const [i, setI] = useState(0);
  if (!images.length) {
    return <div className={`${card} aspect-[16/10] flex items-center justify-center`}><Building2 className="text-[#c9a84c]/25" size={56} /></div>;
  }
  return (
    <div className="space-y-2">
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06] bg-black">
        <AnimatePresence mode="wait">
          <motion.img key={i} src={imgUrl(images[i])} alt={title} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }} className="absolute inset-0 h-full w-full object-cover" />
        </AnimatePresence>
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((u, k) => (
            <button key={u} type="button" onClick={() => setI(k)} aria-label={`${t.photo} ${k + 1}`}
              className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border ${k === i ? 'border-[#c9a84c]' : 'border-white/10 opacity-70 hover:opacity-100'}`}>
              <img src={imgUrl(u)} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#c9a84c]/10"><Icon size={15} className="text-[#c9a84c]" /></span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-[#c9a84c]">{label}</p>
        <p className="text-sm text-white leading-snug">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className={`${card} p-5 space-y-4`} data-testid={testid}>
      <h3 className="text-white font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function InterestModal({ o, onClose, onDone, t, lang }) {
  const [amount, setAmount] = useState(String(o.my_interest?.amount || o.min_contribution));
  const [message, setMessage] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const ok = n >= o.min_contribution && accepted;
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    try {
      await opportunitiesApi.interest(o.id, { amount: n, message: message || null, accepted });
      toast.success(t.toastSent);
      onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="interest-title">
      <motion.form initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-2xl border border-white/10 bg-[#0d1829] p-5 shadow-2xl" data-testid="interest-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="interest-title" className="text-white font-semibold">{t.request}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{opTitle(o, lang)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t.close}><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">{t.amountLabel} ({o.currency === 'USD' ? '$' : '€'})</span>
          <input type="number" min={o.min_contribution} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="interest-amount"
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white focus:border-[#c9a84c]/50 focus:outline-none" />
          <span className={`text-xs ${n && n < o.min_contribution ? 'text-red-300' : 'text-gray-500'}`}>{t.minLabel}: {money(o.min_contribution, o.currency)}</span>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">{t.comment}</span>
          <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000}
            className="w-full resize-y rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#c9a84c]/50 focus:outline-none" />
        </label>
        <label className="flex items-start gap-2.5 text-xs leading-relaxed text-gray-300">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 accent-[#c9a84c]" data-testid="interest-accept" />
          <span>{t.accept}</span>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-gray-300 hover:text-white">{t.cancel}</button>
          <button type="submit" disabled={!ok || busy} data-testid="interest-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} {t.send}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

export default function DashboardPropertyDetail() {
  const { id } = useParams();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [o, setO] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);
  const load = useCallback(() => opportunitiesApi.get(id).then(setO).catch((e) => setError(e.message)), [id]);
  useEffect(() => { setO(null); setError(null); load(); }, [load]);

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/dashboard/propiedades" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"><ArrowLeft size={16} /> {t.back}</Link>
        <EmptyState icon={Building2} title={t.notFound} text={error} />
      </div>
    );
  }
  if (!o) return <PageSkeleton cards={0} rows={5} />;

  const cur = o.currency;
  const ret = returnFigure(o, lang);
  const sale = saleFigure(o, lang);
  const costTotal = (o.cost_breakdown || []).reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const mr = o.market_reference;
  const full = (o.progress?.pct ?? 0) >= 100;

  return (
    <div className="space-y-6" data-testid="opportunity-detail">
      <Link to="/dashboard/propiedades" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"><ArrowLeft size={16} /> {t.back}</Link>
      {lang === 'en' && <p className="-mt-3 text-[11px] text-gray-500" data-testid="content-lang-note">Content delivered in Spanish</p>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(300px,1fr)] items-start">
        <div className="space-y-6 min-w-0">
          <Gallery images={o.images || []} title={opTitle(o, lang)} t={t} />
          <div className="space-y-2">
            <span className={`inline-block rounded-full border px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLE[o.status] || ''}`}>{statusLabel(o.status, lang)}</span>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{opTitle(o, lang)}</h1>
            {o.tagline && <p className="text-xs uppercase tracking-[0.25em] text-[#c9a84c]">{o.tagline}</p>}
            <p className="flex items-center gap-1.5 text-sm text-gray-300"><MapPin size={14} className="text-[#c9a84c]" /> {opPlace(o)}</p>
            {o.description && <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line pt-1">{o.description}</p>}
          </div>

          <Section title={t.facts} testid="op-facts">
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact icon={MapPin} label={t.location} value={opPlace(o)} />
              <Fact icon={Home} label={t.assetType} value={o.asset_type} />
              <Fact icon={Maximize2} label={t.surface} value={o.surface} />
              <Fact icon={Wrench} label={t.projectType} value={o.project_type} />
              <Fact icon={CalendarClock} label={t.term} value={o.term} />
              <Fact icon={Layers} label={t.units} value={o.units} />
              <Fact icon={Coins} label={t.totalInvestment} value={o.total_investment ? money(o.total_investment, cur) : null} />
              <Fact icon={BarChart3} label={t.zoneAvg} value={o.zone_avg_price ? money(o.zone_avg_price, cur) : null} />
              {sale && <Fact icon={Tag} label={sale.label} value={sale.value} />}
              {(o.features || []).map((f) => <Fact key={f.label} icon={ListChecks} label={f.label} value={f.value} />)}
            </div>
          </Section>

          {ret && (
            <section className="rounded-2xl border border-[#c9a84c]/30 bg-gradient-to-br from-[#c9a84c]/[0.12] to-transparent p-5" data-testid="op-return">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#c9a84c]/15"><TrendingUp size={22} className="text-[#e3c87a]" /></span>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[#c9a84c]">{ret.label}</p>
                  <p className="text-3xl font-bold text-[#e3c87a] tabular-nums">{ret.value}</p>
                  {o.scenario && <p className="text-xs text-gray-300 mt-0.5">{t.scenario(o.scenario)}</p>}
                </div>
                {o.return_note && <p className="flex items-start gap-2 text-xs text-gray-300 sm:max-w-[260px] leading-relaxed"><FileText size={14} className="mt-0.5 flex-shrink-0 text-[#c9a84c]" /> {o.return_note}</p>}
              </div>
            </section>
          )}

          {o.cost_breakdown?.length > 0 && (
            <Section title={t.costs} testid="op-costs">
              <div className="overflow-hidden rounded-xl border border-white/[0.06]">
                <table className="w-full text-sm">
                  <tbody>
                    {o.cost_breakdown.map((c) => (
                      <tr key={c.label} className="border-b border-white/[0.04]">
                        <td className="px-4 py-2.5 text-gray-300">{c.label}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{money(c.amount, cur)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#c9a84c]/10">
                      <td className="px-4 py-3 font-semibold text-[#e3c87a]">{t.costTotal}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#e3c87a] tabular-nums">{money(costTotal, cur)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">{t.costNote}</p>
              {(o.vehicle || o.partner) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Fact icon={UserRound} label={t.vehicle} value={o.vehicle} />
                  <Fact icon={Handshake} label={t.operator} value={o.partner ? t.partnerText(o.partner) : null} />
                </div>
              )}
            </Section>
          )}

          {(o.market_note || mr || o.comparables?.length > 0) && (
            <Section title={t.market} testid="op-market">
              {o.market_note && <p className="text-sm text-gray-300 leading-relaxed">{o.market_note}</p>}
              {mr && (
                <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
                  <table className="w-full min-w-[460px] text-sm">
                    <thead><tr className="border-b border-white/[0.06] text-left text-[11px] uppercase tracking-wider text-[#c9a84c]">
                      <th className="px-4 py-2.5">{t.asset}</th><th className="px-4 py-2.5 text-right">{t.avgPrice}</th><th className="px-4 py-2.5 text-right">{mr.range_label || t.range}</th>
                    </tr></thead>
                    <tbody><tr>
                      <td className="px-4 py-3 text-gray-300">{mr.label || '—'}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{mr.average ? money(mr.average, cur) : '—'}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{mr.range_min ? `${money(mr.range_min, cur)} – ${money(mr.range_max, cur)}` : '—'}</td>
                    </tr></tbody>
                  </table>
                </div>
              )}
              {o.comparables?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {o.comparables.map((c) => (
                    <div key={c.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                      <p className="text-[11px] text-gray-400">{c.label}</p>
                      <p className="text-sm font-semibold text-white tabular-nums">{money(c.amount, cur)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}

          <p className="flex items-start gap-2 text-[11px] leading-relaxed text-gray-500"><Info size={13} className="mt-0.5 flex-shrink-0" /> {disclaimer(lang)}</p>
        </div>

        {/* Aportaciones */}
        <aside className={`${card} p-5 space-y-5 lg:sticky lg:top-4`} data-testid="op-funding">
          <h3 className="text-white font-semibold">{t.funding}</h3>
          <FundingBar progress={o.progress} currency={cur} size="lg" />
          <div className={`grid gap-3 ${full ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {!full && <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <p className="text-[11px] text-gray-400">{t.minLabel}</p>
              <p className="text-lg font-bold text-[#e3c87a] tabular-nums" data-testid="op-min">{money(o.min_contribution, cur)}</p>
            </div>}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <p className="text-[11px] text-gray-400">{t.currency}</p>
              <p className="text-lg font-bold text-white">{cur === 'USD' ? t.usd : t.eur}</p>
            </div>
          </div>
          {!full && o.min_reason === 'access' && (
            <p className="text-xs text-gray-400" data-testid="op-min-access">
              {t.accessMin(money(o.min_contribution_base, cur))}
            </p>
          )}
          {o.progress?.target && !full && (
            <p className="text-xs text-gray-400">{t.target} <span className="text-white tabular-nums">{money(o.progress.target, cur)}</span></p>
          )}
          {o.my_interest && (
            <p className="flex items-start gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3 text-xs text-emerald-200">
              <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
              {t.sentFor(money(o.my_interest.amount, cur))}
            </p>
          )}
          {o.accepts_interest ? (
            <button type="button" onClick={() => setAsking(true)} data-testid="op-interest"
              className="w-full rounded-xl bg-[#c9a84c] px-4 py-3 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
              {o.my_interest ? t.change : t.request}
            </button>
          ) : (
            <p className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-xs text-gray-300">
              {full ? t.full : t.closed}
            </p>
          )}
          <p className="text-[11px] leading-relaxed text-gray-500">{t.footer}</p>
        </aside>
      </div>

      {asking && <InterestModal o={o} t={t} lang={lang} onClose={() => setAsking(false)} onDone={() => { setAsking(false); load(); }} />}
    </div>
  );
}
